import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  ReactNode,
} from "react";
import { dashboardApi, productsApi, ebayApi } from "../services/api";
import { useAuth } from "./AuthContext";
import { useStore } from "./StoreContext";
import { POLLING_INTERVALS, createSmartInterval } from "../utils/polling";

// Cache TTL in milliseconds (5 minutes)
const CACHE_TTL = 5 * 60 * 1000;

type PeriodType = "TODAY" | "7D" | "1M" | "6M" | "1Y" | "ALL";

interface DashboardStats {
  // Basic stats
  totalOrders: number;
  totalRevenue: number;
  totalProfit: number;
  monthlyOrders: number;
  monthlyRevenue: number;
  monthlyProfit: number;
  todayOrders: number;
  todayRevenue: number;
  todayEarnings: number;
  todayProfit: number;
  pendingOrders: number;
  shippedOrders: number;
  deliveredOrders: number;
  cancelledOrders: number;
  dailyAvgOrders: number;
  dailyAvgRevenue: number;
  dailyAvgProfit: number;
  successRate: number;
  avgProfitMargin: number;
  trackingStartedAt: string | null;
  lastCalculatedAt: string | null;

  // Extended stats
  widgets: any[];
  profitWidgets: any[];
  recentOrders: any[];
  bestSelling: any[];
  revenueData: {
    labels: string[];
    orders: number[];
    earnings: number[];
    profit: number[];
  };
  statusCounts: {
    [key: string]: number;
  };
  salesByCategory: any[];

  // Dropshipping specific
  activeListings?: number;
  oosCount?: number; // Out of stock count
  supplierPerformance?: any[];
}

interface SyncStatus {
  status: string;
  progress: number;
  synced_products?: number;
  total_products?: number;
}

interface DashboardContextType {
  stats: DashboardStats | null;
  loading: boolean;
  fullStatsLoaded: boolean;
  error: string | null;
  period: PeriodType;
  setPeriod: (period: PeriodType) => void;
  refreshStats: () => Promise<void>;
  loadFullStats: () => Promise<void>;
  refreshLimits: () => Promise<void>;
  toShipCount: number;
  activeListings: number;
  oosCount: number;
  managedProducts: number;
  maxProducts: number;
  syncStatus: SyncStatus | null;
}

const defaultStats: DashboardStats = {
  totalOrders: 0,
  totalRevenue: 0,
  totalProfit: 0,
  monthlyOrders: 0,
  monthlyRevenue: 0,
  monthlyProfit: 0,
  todayOrders: 0,
  todayRevenue: 0,
  todayEarnings: 0,
  todayProfit: 0,
  pendingOrders: 0,
  shippedOrders: 0,
  deliveredOrders: 0,
  cancelledOrders: 0,
  dailyAvgOrders: 0,
  dailyAvgRevenue: 0,
  dailyAvgProfit: 0,
  successRate: 100,
  avgProfitMargin: 20.0, // Default margin for dropshipping
  trackingStartedAt: null,
  lastCalculatedAt: null,
  widgets: [],
  profitWidgets: [],
  recentOrders: [],
  bestSelling: [],
  revenueData: {
    labels: [],
    orders: [],
    earnings: [],
    profit: [],
  },
  statusCounts: {},
  salesByCategory: [],
  activeListings: 0,
  oosCount: 0,
  supplierPerformance: [],
};

const DashboardContext = createContext<DashboardContextType>({
  stats: null,
  loading: true,
  fullStatsLoaded: false,
  error: null,
  period: "7D", // Default: Last 7 Days
  setPeriod: () => {},
  refreshStats: async () => {},
  loadFullStats: async () => {},
  refreshLimits: async () => {},
  toShipCount: 0,
  activeListings: 0,
  oosCount: 0,
  managedProducts: 0,
  maxProducts: 100,
  syncStatus: null,
});

export const useDashboard = () => useContext(DashboardContext);

interface DashboardProviderProps {
  children: ReactNode;
}

export const DashboardProvider: React.FC<DashboardProviderProps> = ({
  children,
}) => {
  const { isAuthenticated, token } = useAuth();
  const { selectedStore } = useStore();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [fullStatsLoaded, setFullStatsLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toShipCount, setToShipCount] = useState(0);
  const [activeListings, setActiveListings] = useState(0);
  const [oosCount, setOosCount] = useState(0);
  const [managedProducts, setManagedProducts] = useState(0);
  const [maxProducts, setMaxProducts] = useState(100);
  const [period, setPeriodState] = useState<PeriodType>("7D");
  const [syncStatus, setSyncStatus] = useState<SyncStatus | null>(null);
  const [lastFetched, setLastFetched] = useState<Date | null>(null);
  const lastSyncedCountRef = useRef<number>(0);
  const lastPeriodRef = useRef<PeriodType | null>(null);
  const lastStoreIdRef = useRef<number | undefined>(undefined);
  const fetchingRef = useRef<boolean>(false);

  // Fetch listing counts
  const fetchListingCounts = useCallback(async () => {
    // Don't fetch if not authenticated
    if (!isAuthenticated) {
      return;
    }

    try {
      const data = await dashboardApi.getListingCounts(selectedStore?.id);
      if (data.success) {
        setActiveListings(data.activeListings || 0);
        setOosCount(data.oosCount || 0);
      }
    } catch (err: any) {
      // Don't log 401 errors - user just logged out
      if (err.message !== "Unauthorized") {
        console.error("Failed to fetch listing counts:", err);
      }
    }
  }, [isAuthenticated, selectedStore?.id]);

  // Fetch product limits (managed products / max products) - user-wide, not per-store
  const fetchProductLimits = useCallback(async () => {
    if (!isAuthenticated) {
      return;
    }

    try {
      const response = await productsApi.getLimits();
      if (response?.success && response?.data) {
        setManagedProducts(response.data.current_products || 0);
        setMaxProducts(response.data.max_products || 100);
      }
    } catch (err: any) {
      if (err.message !== "Unauthorized") {
        console.error("Failed to fetch product limits:", err);
      }
    }
  }, [isAuthenticated]);

  // Fetch full dashboard stats
  const fetchFullStats = useCallback(async (selectedPeriod: PeriodType, force = false) => {
    // Don't fetch if not authenticated
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }

    // Prevent concurrent fetches
    if (fetchingRef.current) return;

    // Check if store changed - force refresh if so
    const storeChanged = lastStoreIdRef.current !== selectedStore?.id;
    if (storeChanged) {
      lastStoreIdRef.current = selectedStore?.id;
      force = true; // Force refresh on store change
    }

    // Check if cache is still valid (unless force refresh or period changed)
    if (!force && lastFetched && lastPeriodRef.current === selectedPeriod) {
      const cacheAge = Date.now() - lastFetched.getTime();
      if (cacheAge < CACHE_TTL) {
        return; // Cache is still valid
      }
    }

    fetchingRef.current = true;

    try {
      setLoading(true);
      setError(null);

      // Use cached stats for better performance
      const data = await dashboardApi.getCachedStats(selectedPeriod, selectedStore?.id);

      if (data.success && data.stats) {
        const apiStats = data.stats;

        setStats({
          ...defaultStats,
          totalOrders: apiStats.totalOrders || 0,
          totalRevenue: apiStats.totalEarnings || apiStats.totalRevenue || 0,
          totalProfit: apiStats.totalProfit || 0,
          monthlyOrders: apiStats.monthlyOrders || 0,
          monthlyRevenue:
            apiStats.monthlyEarnings || apiStats.monthlyRevenue || 0,
          monthlyProfit: apiStats.monthlyProfit || 0,
          todayOrders: apiStats.todayOrders || 0,
          todayRevenue: apiStats.todayEarnings || apiStats.todayRevenue || 0,
          todayEarnings: apiStats.todayEarnings || 0,
          todayProfit: apiStats.todayProfit || 0,
          pendingOrders: apiStats.statusCounts?.["Pending"] || 0,
          shippedOrders: apiStats.statusCounts?.["Shipped"] || 0,
          deliveredOrders: apiStats.statusCounts?.["Delivered"] || 0,
          cancelledOrders: apiStats.statusCounts?.["Cancelled"] || 0,
          dailyAvgOrders: apiStats.dailyAvgOrders || 0,
          dailyAvgRevenue: apiStats.dailyAvgRevenue || 0,
          dailyAvgProfit: apiStats.dailyAvgProfit || 0,
          successRate: apiStats.successRate || 100,
          avgProfitMargin: apiStats.avgProfitMargin || 20.0,
          trackingStartedAt: apiStats.trackingStartedAt || null,
          lastCalculatedAt: apiStats.lastCalculatedAt || null,
          widgets: apiStats.widgets || [],
          profitWidgets: apiStats.profitWidgets || [],
          recentOrders: apiStats.recentOrders || [],
          bestSelling: apiStats.bestSelling || [],
          revenueData: apiStats.revenueData || defaultStats.revenueData,
          statusCounts: apiStats.statusCounts || {},
          salesByCategory: apiStats.salesByCategory || [],
          activeListings: apiStats.activeListings || 0,
          oosCount: apiStats.oosCount || 0,
          supplierPerformance: apiStats.supplierPerformance || [],
        });

        setFullStatsLoaded(true);

        // Update separate state values
        setToShipCount(apiStats.statusCounts?.["To Ship"] || 0);
        setActiveListings(apiStats.activeListings || 0);
        setOosCount(apiStats.oosCount || 0);

        // Update cache tracking
        setLastFetched(new Date());
        lastPeriodRef.current = selectedPeriod;
      }
    } catch (err: any) {
      // Don't log or set error for 401 - user just logged out
      if (err.message === "Unauthorized") {
        setStats(null);
        setLoading(false);
        fetchingRef.current = false;
        return;
      }
      console.error("Failed to fetch dashboard stats:", err);
      setError(err.message || "Failed to load dashboard data");
      // Set default stats on error so UI doesn't break
      setStats(defaultStats);
    } finally {
      setLoading(false);
      fetchingRef.current = false;
    }
  }, [lastFetched, isAuthenticated, selectedStore?.id]);

  // Set period and fetch stats (period change always forces new fetch)
  const setPeriod = useCallback(
    (newPeriod: PeriodType) => {
      setPeriodState(newPeriod);
      fetchFullStats(newPeriod, true);
    },
    [fetchFullStats]
  );

  // Load full stats for current period
  const loadFullStats = useCallback(async () => {
    await fetchFullStats(period);
  }, [fetchFullStats, period]);

  // Refresh all stats (force = true bypasses cache)
  const refreshStats = useCallback(async (force = true) => {
    setLoading(true);
    setError(null);
    await fetchFullStats(period, force);
    await fetchListingCounts();
    await fetchProductLimits();
    setLoading(false);
  }, [fetchFullStats, fetchListingCounts, fetchProductLimits, period]);

  // Fetch data when user logs in (token changes)
  useEffect(() => {
    let mounted = true;

    const initLoad = async () => {
      // Only load if authenticated
      if (mounted && isAuthenticated && token) {
        setLoading(true);
        await fetchFullStats(period, true); // Force on login
        await fetchListingCounts();
        await fetchProductLimits();
        setLoading(false);
      } else if (!isAuthenticated) {
        // User logged out - reset state
        setStats(null);
        setLoading(false);
      }
    };

    initLoad();

    return () => {
      mounted = false;
    };
  }, [isAuthenticated, token]); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-fetch when store changes
  useEffect(() => {
    if (isAuthenticated && selectedStore?.id) {
      // Reset state for new store
      setLoading(true);
      fetchFullStats(period, true); // Force refresh on store change
      fetchListingCounts();
      fetchProductLimits();
    }
  }, [selectedStore?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-refresh stats every 5 minutes (matches cache TTL)
  useEffect(() => {
    const cleanup = createSmartInterval(
      () => {
        // Only refresh if still authenticated
        if (isAuthenticated) {
          fetchFullStats(period, true); // Force refresh on timer
        }
      },
      POLLING_INTERVALS.DASHBOARD_STATS,
      { runImmediately: false, runOnVisible: true }
    );

    return () => cleanup();
  }, [period, fetchFullStats, isAuthenticated]);

  // Poll sync status and refresh stats during sync
  useEffect(() => {
    if (!isAuthenticated) return;

    let intervalId: NodeJS.Timeout | null = null;
    let wasCompleted = false;

    const checkSyncStatus = async () => {
      try {
        const status = await ebayApi.getSyncStatus();

        // Check if sync just completed
        if (syncStatus?.status === 'syncing' && status.status === 'completed' && !wasCompleted) {
          wasCompleted = true;
          // Refresh all stats when sync completes
          setTimeout(() => {
            fetchFullStats(period);
            fetchListingCounts();
            fetchProductLimits();
          }, 1000);
        }

        setSyncStatus(status);

        // If syncing and product count changed, refresh listing counts
        if (status.status === 'syncing') {
          const currentSynced = status.synced_products || 0;
          if (currentSynced !== lastSyncedCountRef.current) {
            lastSyncedCountRef.current = currentSynced;
            // Fetch updated listing counts
            await fetchListingCounts();
          }
        }
      } catch (e) {
        // Ignore errors
      }
    };

    // Initial check
    checkSyncStatus();

    // Poll more frequently when syncing (5 seconds), slower when idle (60 seconds - optimized)
    const isSyncing = syncStatus?.status === 'syncing';
    const pollInterval = isSyncing ? POLLING_INTERVALS.SYNC_STATUS_ACTIVE : POLLING_INTERVALS.SYNC_STATUS_IDLE;

    intervalId = setInterval(checkSyncStatus, pollInterval);

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [syncStatus?.status, period, fetchFullStats, fetchListingCounts, fetchProductLimits]);

  return (
    <DashboardContext.Provider
      value={{
        stats,
        loading,
        fullStatsLoaded,
        error,
        period,
        setPeriod,
        refreshStats,
        loadFullStats,
        refreshLimits: fetchProductLimits,
        toShipCount,
        activeListings,
        oosCount,
        managedProducts,
        maxProducts,
        syncStatus,
      }}
    >
      {children}
    </DashboardContext.Provider>
  );
};

export default DashboardContext;
